import { z } from 'zod';
import { clientTypeSchema, projectStatusSchema, userRoleSchema } from './enums.js';
import { permissionSchema } from './permission.js';

export const assignTechnicianSchema = z.object({
  technicianId: z.string().uuid().nullable(),
});
export type AssignTechnicianInput = z.infer<typeof assignTechnicianSchema>;

export const updateProjectStatusSchema = z.object({
  status: projectStatusSchema,
  message: z.string().trim().max(500).optional(),
});
export type UpdateProjectStatusInput = z.infer<typeof updateProjectStatusSchema>;

const phoneRegexClient = /^[+0-9 ().-]{6,20}$/;

export const updateClientSchema = z
  .object({
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    phone: z.string().trim().regex(phoneRegexClient).optional(),
    clientType: clientTypeSchema.optional(),
  })
  .refine(
    (d) =>
      d.firstName !== undefined ||
      d.lastName !== undefined ||
      d.phone !== undefined ||
      d.clientType !== undefined,
    { message: 'Au moins un champ doit être renseigné' },
  );
export type UpdateClientInput = z.infer<typeof updateClientSchema>;

export const createAppointmentSchema = z.object({
  projectId: z.string().uuid(),
  technicianId: z.string().uuid(),
  scheduledAt: z.string().datetime(),
  durationMinutes: z.number().int().positive().max(720).default(90),
  notes: z.string().max(500).optional(),
});
export type CreateAppointmentInput = z.infer<typeof createAppointmentSchema>;

export const createReportSchema = z.object({
  projectId: z.string().uuid(),
  title: z.string().trim().min(1).max(200),
  content: z.string().trim().min(1).max(20_000),
});
export type CreateReportInput = z.infer<typeof createReportSchema>;

/** Taille max d'un PDF de compte rendu (25 Mo). */
export const REPORT_FILE_MAX_BYTES = 25 * 1024 * 1024;

// Le technicien dépose un compte rendu : il demande d'abord une URL pré-signée
// (le PDF part en direct vers S3, jamais via notre serveur), puis soumet le
// rapport (clé du fichier + note WYSIWYG optionnelle, assainie côté serveur).
export const requestReportUploadSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  contentType: z.literal('application/pdf'),
  sizeBytes: z.number().int().positive().max(REPORT_FILE_MAX_BYTES),
});
export type RequestReportUploadInput = z.infer<typeof requestReportUploadSchema>;

export const submitReportSchema = z.object({
  title: z.string().trim().min(1).max(200).default("Compte rendu d'intervention"),
  // HTML produit par l'éditeur WYSIWYG ; assaini (allowlist) côté serveur.
  noteHtml: z.string().max(50_000).optional(),
  fileS3Key: z.string().trim().min(1).max(1024),
  fileName: z.string().trim().min(1).max(255),
  fileContentType: z.literal('application/pdf'),
  fileSizeBytes: z.number().int().positive().max(REPORT_FILE_MAX_BYTES),
});
export type SubmitReportInput = z.infer<typeof submitReportSchema>;

// --- Staff (super-admin only)

const staffRoleSchema = z.enum(['admin', 'super_admin', 'technicien']);

const phoneRegex = /^[+0-9 ().-]{6,20}$/;

export const createStaffSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(8).max(200),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  phone: z.string().trim().regex(phoneRegex),
  role: staffRoleSchema,
  // Cochées par le super_admin. Ignorées si le rôle n'est pas `admin`.
  permissions: permissionSchema.array().default([]),
});
export type CreateStaffInput = z.infer<typeof createStaffSchema>;

export const updateStaffSchema = z
  .object({
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    phone: z.string().trim().regex(phoneRegex).optional(),
    role: staffRoleSchema.optional(),
    permissions: permissionSchema.array().optional(),
  })
  .refine(
    (data) =>
      data.firstName !== undefined ||
      data.lastName !== undefined ||
      data.phone !== undefined ||
      data.role !== undefined ||
      data.permissions !== undefined,
    { message: 'Au moins un champ doit être renseigné' },
  );
export type UpdateStaffInput = z.infer<typeof updateStaffSchema>;

// --- Gestion unifiée des utilisateurs (super_admin) ---

export const userTypeFilterSchema = z.enum([
  'all',
  'client',
  'collectivite',
  'partenaire',
  'technicien',
  'staff',
]);
export type UserTypeFilter = z.infer<typeof userTypeFilterSchema>;

export const updateUserSchema = z
  .object({
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    phone: z.string().trim().regex(phoneRegex).optional(),
    role: userRoleSchema.optional(),
    clientType: clientTypeSchema.nullable().optional(),
    permissions: permissionSchema.array().optional(),
  })
  .refine(
    (data) =>
      data.firstName !== undefined ||
      data.lastName !== undefined ||
      data.phone !== undefined ||
      data.role !== undefined ||
      data.clientType !== undefined ||
      data.permissions !== undefined,
    { message: 'Au moins un champ doit être renseigné' },
  );
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// On expose le sous-ensemble UserRole côté staff, distinct du UserRole global
// pour éviter qu'un client soit créé via /staff.
export { userRoleSchema };
